// Pauses the reader makes that the text does not ask for.
//
// The level of every 10 ms frame is its peak, as a share of the loud level of
// the track (the 95th percentile of frame peaks). Peaks, not energy, because
// that is what the waveform on screen draws: a threshold line on it is then
// exact — a frame whose wave stays under the line is silence, and nothing
// else is. A run of quiet frames is a pause. One longer than the text calls
// for loses its MIDDLE: the decay of the word before and the breath before
// the next word stay, and the join cannot be heard.
//
// How long a pause may be depends on what the text puts there. After a full
// stop a longer one is natural; after a comma or nothing, a short one. The
// word timings (alignEdit.js) say which word ends before the pause, and the
// text between that word and the next says what punctuation stands there.
//
// CommonJS: the browser editor (src/utils/pauses.js) and the server-side task
// runner (tasks/recipes/pause_cleanup.js) read the same pauses the same way.

const HOP_SEC = 0.01;

// {frames, loud}: the peak of every frame as a share of `loud`, and `loud`
// itself in sample units (what a share of it is on the waveform).
function levelFrames(samples, sampleRate, hopSec = HOP_SEC) {
  const hop = Math.max(1, Math.round(hopSec * sampleRate));
  const n = Math.floor(samples.length / hop);
  const peaks = new Float32Array(n);
  for (let i = 0; i < n; i += 1) {
    const off = i * hop;
    let m = 0;
    for (let j = 0; j < hop; j += 1) { const v = Math.abs(samples[off + j]); if (v > m) m = v; }
    peaks[i] = m;
  }
  const sorted = Float32Array.from(peaks).sort();
  const loud = n ? sorted[Math.min(n - 1, Math.floor(0.95 * n))] : 0;
  const frames = new Float32Array(n);
  for (let i = 0; i < n; i += 1) frames[i] = peaks[i] / (loud || 1);
  return { frames, loud };
}

const round3 = (x) => Math.round(x * 1000) / 1000;

// Quiet runs at least `minSec` long inside [from, to], as {start, end} in
// seconds. A run that reaches the last frame is extended to `duration`, so a
// silent tail is not short of the end by one frame.
// `threshold` is a share of the loud level, like the frames.
function findPauses(frames, { threshold, minSec, from = 0, to = Infinity, duration = Infinity, hopSec = HOP_SEC }) {
  const runs = [];
  let i = 0;
  while (i < frames.length) {
    if (frames[i] < threshold) {
      let j = i;
      while (j < frames.length && frames[j] < threshold) j += 1;
      const start = Math.max(i * hopSec, from);
      const reachesEnd = j >= frames.length && Number.isFinite(duration);
      const end = Math.min(reachesEnd ? duration : j * hopSec, to);
      if (end - start >= minSec - 1e-9) runs.push({ start: round3(start), end: round3(end) });
      i = j;
    } else i += 1;
  }
  return runs;
}

// What stands at this pause in the text:
//   'sentence' — a full stop, ?, !, … or a line break before the next word
//   'phrase'   — anything else between two words (a comma, a dash, nothing)
//   'lead'     — before the first word, 'tail' — after the last
//   'any'      — no timings at all, so nothing is known
function pauseKind(pause, words, text, duration = Infinity) {
  const spoken = (words || []).filter((w) => !w.d);
  if (!spoken.length) {
    if (pause.start <= HOP_SEC) return 'lead';
    if (Number.isFinite(duration) && pause.end >= duration - HOP_SEC) return 'tail';
    return 'any';
  }
  const mid = (pause.start + pause.end) / 2;
  let before = -1;
  for (let k = 0; k < spoken.length; k += 1) {
    if (spoken[k].e <= mid) before = k; else break;
  }
  if (before < 0) return 'lead';
  if (before === spoken.length - 1) return 'tail';
  const between = (text || '').slice(spoken[before].b, spoken[before + 1].a);
  return /[.!?…\n]/.test(between) ? 'sentence' : 'phrase';
}

// The cuts that bring every pause down to its cap. `caps` is seconds per
// kind: {sentence, phrase, lead, tail, any}. A pause between words loses its
// middle; one at an edge of the track keeps its `keep` next to the speech.
// A cut shorter than MIN_CUT_SEC is not worth a join and is skipped.
const MIN_CUT_SEC = 0.05;
function planPauseCuts(pauses, words, text, caps, duration = Infinity) {
  const cuts = [];
  for (const p of pauses) {
    const kind = pauseKind(p, words, text, duration);
    const keep = caps[kind] != null ? caps[kind] : caps.any;
    const len = p.end - p.start;
    if (!(len - keep >= MIN_CUT_SEC)) continue;
    let start;
    let end;
    if (kind === 'lead') { start = p.start; end = p.end - keep; }
    else if (kind === 'tail') { start = p.start + keep; end = p.end; }
    else { start = p.start + keep / 2; end = p.end - keep / 2; }
    cuts.push({ start: round3(start), end: round3(end), kind, pause: p });
  }
  return cuts;
}

const cutsTotal = (cuts) => cuts.reduce((n, c) => n + (c.end - c.start), 0);

// What a preview plays, as segments of the track in order (audioEdit.assemble):
//   'remains' — [from, to] without the cuts
//   'cut'     — the cuts alone, a breath of silence between them
//   'joins'   — a second before and a second after each cut, already joined,
//               a breath of silence between one join and the next
function previewSegments(mode, cuts, from, to, around = 1, gap = 0.5) {
  const sorted = [...cuts].sort((x, y) => x.start - y.start);
  if (mode === 'cut') return sorted.map((c, i) => ({ start: c.start, end: c.end, gapBefore: i ? gap : 0 }));
  if (mode === 'joins') {
    const out = [];
    sorted.forEach((c, i) => {
      const a = Math.max(from, c.start - around);
      const b = Math.min(to, c.end + around);
      if (a < c.start) out.push({ start: a, end: c.start, gapBefore: i ? gap : 0 });
      if (c.end < b) out.push({ start: c.end, end: b, gapBefore: 0 });
    });
    return out;
  }
  const out = [];
  let at = from;
  for (const c of sorted) {
    if (c.start > at) out.push({ start: at, end: c.start, gapBefore: 0 });
    at = Math.max(at, c.end);
  }
  if (to > at) out.push({ start: at, end: to, gapBefore: 0 });
  return out;
}

module.exports = {
  HOP_SEC, MIN_CUT_SEC, levelFrames, findPauses, pauseKind, planPauseCuts, cutsTotal, previewSegments,
};
